// Relative imports (not `@/`) so bun can test this module.
import type { RuleSet } from '../rules';
import {
  type EntityId,
  fromMilli,
  gatherResource,
  type PlayerId,
  type SimContext,
  type SimState,
  territoryOwners,
  tileResources,
  toMilli,
  type Unit,
} from '../sim';
import { cityName } from './cities';
import { type HealerStatus, healerStatus } from './heals';
import type { Names } from './names';

/**
 * What `unitActivity` and `unitStatus` read besides the unit: build it once
 * per frame with `statusEnv` (the shelter set and healer lookups are shared
 * by every unit's row).
 */
export interface StatusEnv {
  state: SimState;
  ctx: SimContext;
  playerId: PlayerId;
  playerName: (id: PlayerId) => string;
  /** Own territory that shelters units: an auto worker heading into it from outside is retreating. */
  shelters: ReadonlySet<string>;
  /** Soldiers idle outside a full barracks (`waitingOutside`), flattened. */
  waiting: ReadonlySet<EntityId>;
  heals: ReadonlyMap<EntityId, EntityId>;
  healers: Map<EntityId, HealerStatus | null>;
  /** The player's own city names (client-local). */
  names?: Names;
}

export function statusEnv(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  playerName: (id: PlayerId) => string,
  /** This tick's `healings` (`tickHealings`). */
  heals: ReadonlyMap<EntityId, EntityId>,
  waiting: ReadonlySet<EntityId> = new Set(),
  names?: Names
): StatusEnv {
  const { rules } = ctx;
  const shelters = new Set<string>();
  for (const [key, cityId] of territoryOwners(state, rules)) {
    const city = state.buildings[cityId];
    if (city?.owner === playerId && rules.buildings[city.type]?.garrison) shelters.add(key);
  }
  return {
    state,
    ctx,
    playerId,
    playerName,
    shelters,
    waiting,
    heals,
    healers: new Map(),
    names,
  };
}

function tending(env: StatusEnv, u: Unit): HealerStatus | null {
  if (!env.healers.has(u.id)) {
    env.healers.set(u.id, healerStatus(env.state, env.ctx, u, env.heals));
  }
  return env.healers.get(u.id) ?? null;
}

/** Short activity word, shared by a unit's own row and its group's summary. */
export function unitActivity(env: StatusEnv, u: Unit): string {
  const { state, ctx } = env;
  const { rules, terrain } = ctx;
  // Inside a building: a farm or forestry's workers farm (below), others wait in it.
  const home = u.inside ? state.buildings[u.inside] : undefined;
  const homeDef = home && rules.buildings[home.type];
  if (u.inside && !homeDef?.provides) return `in ${(homeDef?.name ?? 'building').toLowerCase()}`;
  const automate = u.auto ? rules.units[u.type]?.automate : undefined;
  // A spy: exposed, blended into a rival city, or hidden (moving or not).
  if (rules.units[u.type]?.stealth) {
    const moving = !!u.path && u.path.length > 0;
    if (u.exposed) return moving ? 'exposed · moving' : 'exposed';
    if (u.disguise) return 'disguised';
    return moving ? 'hidden · moving' : 'hidden';
  }
  if (u.order?.kind === 'attack') return u.path && u.path.length > 0 ? 'closing in' : 'attacking';
  // Healers tend the wounded on their own (sim tend.ts): mending, on their
  // way to a patient, about to pick one, or idle with nobody hurt nearby.
  const healer = tending(env, u);
  if (healer) return healer.activity;
  if (automate === 'explore') {
    if (u.auto?.phase === 'flee') return 'retreating';
    if (u.auto?.phase === 'patrol') return 'patrolling';
    // Waiting out danger includes trying other ways round it (the dare
    // timer running), so the status doesn't flicker between the two.
    if (u.auto?.blocked || (u.auto?.waitSince !== undefined && !u.auto.dare)) return 'blocked';
    if (u.auto?.retryTick === undefined) return 'exploring';
    return 'done';
  }
  const moving = !!u.path && u.path.length > 0;
  if (u.carry && !moving && u.carry.unloading !== undefined) return 'unloading';
  if (
    u.carry &&
    moving &&
    (u.auto?.phase === 'haul' || (u.order?.kind === 'gather' && u.order.haul))
  ) {
    return 'hauling';
  }
  if (moving) {
    if (u.order?.kind === 'attackMove') return 'attack-moving';
    if (automate !== 'work') return 'moving';
    const t = u.auto?.target;
    const inbound =
      t && env.shelters.has(`${t.q},${t.r}`) && !env.shelters.has(`${u.pos.q},${u.pos.r}`);
    return inbound ? 'retreating' : 'heading to work';
  }
  if (u.order?.kind === 'build') return 'building';
  if (gatherResource(state, { terrain, rules }, u)) {
    return u.inside ? 'farming' : 'gathering';
  }
  const capacity = rules.units[u.type]?.carryCapacity;
  if (u.carry && capacity !== undefined && u.carry.amount >= toMilli(capacity)) return 'full';
  if (automate === 'work' && u.auto?.retryTick !== undefined) return 'no work found';
  if (env.waiting.has(u.id)) return 'waiting outside';
  return 'idle';
}

/** "food" for a unit's carried load. */
function loadName(rules: RuleSet, u: Unit): string {
  const res = u.carry?.res ?? '';
  return (rules.resources[res]?.name ?? res).toLowerCase();
}

/** A unit's status line: its activity, with what it's carrying, gathering or tending. */
export function unitStatus(env: StatusEnv, u: Unit): string {
  const { state, ctx, playerId } = env;
  const { rules } = ctx;
  const a = unitActivity(env, u);
  if (a === 'done') return 'nothing left to explore';
  if (a === 'disguised') {
    const city = state.buildings[u.disguise?.cityId ?? ''];
    return city
      ? `disguised in ${cityName(city, playerId, new Map(), env.playerName, env.names)}`
      : a;
  }
  if (a === 'blocked') return 'waiting: area is dangerous';
  if (a === 'waiting outside') return 'waiting outside: barracks full';
  if (a === 'hauling' || a === 'unloading') return `${a} ${loadName(rules, u)}`;
  if (a === 'full') return `full · ${loadName(rules, u)}`;
  const healer = tending(env, u);
  if (healer) return healer.text;
  if (a !== 'farming' && a !== 'gathering') return a;
  const resource = gatherResource(state, ctx, u) ?? '';
  const name = (rules.resources[resource]?.name ?? resource).toLowerCase();
  if (a === 'farming') return `farming ${name}`;
  const left = tileResources(state, ctx, u.pos)?.remaining ?? 0;
  return `gathering ${name} · ${Math.ceil(fromMilli(left))} left`;
}
